import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useLocation, useSearch } from 'wouter';
import { loadStripe, type Stripe, type StripeElements } from '@stripe/stripe-js';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import CheckoutShell from '@/components/checkout/CheckoutShell';
import CheckoutAlert from '@/components/checkout/CheckoutAlert';
import CheckoutConfirmation from '@/components/checkout/CheckoutConfirmation';
import OrderSummaryCard from '@/components/checkout/OrderSummaryCard';
import { buttonClass } from '@/components/checkout/CheckoutControls';
import LoginStep from '@/components/checkout/steps/LoginStep';
import ContactInformationStep from '@/components/checkout/steps/ContactInformationStep';
import AcceleratorStep from '@/components/checkout/steps/AcceleratorStep';
import PaymentStep from '@/components/checkout/steps/PaymentStep';
import CompletePurchaseStep, {
  type PurchaseStatus,
} from '@/components/checkout/steps/CompletePurchaseStep';
import {
  DEFAULT_PLAN_ID,
  buildOrderSummary,
  toCheckoutPlan,
  toCouponDefinition,
  type CouponDefinition,
} from '@/lib/checkout/pricing';
import { checkoutApi, CheckoutApiError, type CatalogueResponse, type PaymentResponse } from '@/lib/checkout/api';
import { clearDraft, loadDraft, saveDraft } from '@/lib/checkout/draftStorage';
import { CHECKOUT_STEPS, emptyDraft, type CheckoutDraft, type CheckoutStepId, type ContactDraft } from '@/lib/checkout/types';

const STEP_LABEL: Record<CheckoutStepId, string> = {
  login: 'Create your login',
  contact: 'Contact information',
  accelerator: 'Add the Accelerator course',
  payment: 'Payment',
  complete: 'Complete membership purchase',
};

/** How long step 5 waits for AIVIA to see the payment before saying it is in hand. */
const PAID_POLL_ATTEMPTS = 20;
const PAID_POLL_MS = 1500;

interface PaymentFailure {
  title: string;
  message: string;
}

type StripeBridge = MutableRefObject<{ stripe: Stripe | null; elements: StripeElements | null }>;

/** Exposes the Stripe instance and Elements to the page, which sits outside `<Elements>`. */
function StripeBridgeSetter({ bridge }: { bridge: StripeBridge }) {
  const stripe = useStripe();
  const elements = useElements();
  bridge.current = { stripe, elements };
  return null;
}

const messageOf = (error: unknown) =>
  error instanceof CheckoutApiError ? error.message : 'Something went wrong. Please try again.';

const contactPayload = (c: ContactDraft) => ({
  legalName: c.legalName,
  billing: c.billing,
  mailingSameAsBilling: c.mailingSameAsBilling,
  mailing: c.mailingSameAsBilling ? undefined : c.mailing,
  phone: c.phone,
  agencyName: c.agencyName,
  hasAssociates: c.hasAssociates,
  associateCount: c.associateCount,
  referredBy: c.referredBy,
});

/**
 * Member checkout (AR-290 screens, AR-420 wiring) — five screens driven as one
 * continuous session. Each step is saved on the server as the member moves on
 * (account-first), and also kept in the browser so a refresh lands on the same
 * screen. The card goes from Stripe's Payment Element straight to Stripe; the
 * member is told "complete" only once the server reports the checkout paid.
 */
export default function Checkout() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const planParam = new URLSearchParams(search).get('plan');

  const [catalogue, setCatalogue] = useState<CatalogueResponse | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [draft, setDraft] = useState<CheckoutDraft>(() => emptyDraft());
  const [planKey, setPlanKey] = useState<string>((planParam ?? DEFAULT_PLAN_ID).toLowerCase());
  const [coupon, setCoupon] = useState<CouponDefinition | null>(null);
  const [resumed, setResumed] = useState<CheckoutDraft | null>(null);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [payment, setPayment] = useState<PaymentResponse | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [purchaseStatus, setPurchaseStatus] = useState<PurchaseStatus>('idle');
  const [paymentFailure, setPaymentFailure] = useState<PaymentFailure | null>(null);
  const [purchased, setPurchased] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const bridge: StripeBridge = useRef({ stripe: null, elements: null });

  const plans = catalogue?.plans ?? [];
  const planRow = plans.find((p) => p.key === planKey) ?? plans.find((p) => p.key === DEFAULT_PLAN_ID) ?? plans[0];
  const plan = useMemo(() => (planRow ? toCheckoutPlan(planRow) : null), [planRow]);
  // Null when the server could not load the Accelerator price: the add-on is
  // then unavailable and its step is skipped, never offered at $0.
  const acceleratorCents = catalogue?.accelerator?.priceCents ?? null;
  const acceleratorAvailable = acceleratorCents !== null;
  const stripePromise = useMemo(
    () => (catalogue?.publishableKey ? loadStripe(catalogue.publishableKey) : null),
    [catalogue?.publishableKey],
  );
  const order = useMemo(
    () =>
      plan
        ? buildOrderSummary({
            plan,
            acceleratorAdded: acceleratorAvailable && draft.accelerator.added,
            acceleratorCents: acceleratorCents ?? 0,
            coupon,
          })
        : null,
    [plan, draft.accelerator.added, acceleratorAvailable, acceleratorCents, coupon],
  );

  // Catalogue, then whatever this browser's checkout already holds on the server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let cat: CatalogueResponse;
      try {
        cat = await checkoutApi.catalogue();
        if (cancelled) return;
        setCatalogue(cat);
      } catch (error) {
        if (!cancelled) setCatalogueError(messageOf(error));
        return;
      }

      const stored = loadDraft();
      let server: Awaited<ReturnType<typeof checkoutApi.status>> | null = null;
      try {
        server = await checkoutApi.status();
      } catch {
        server = null; // no checkout bound to this browser (or it expired)
      }
      if (cancelled) return;

      if (server?.status === 'paid') {
        clearDraft();
        setDraft((d) => ({ ...d, login: { ...d.login, email: server!.email } }));
        if (server.plan) setPlanKey(server.plan.key);
        setPurchased(true);
        return;
      }
      if (server) {
        // Never past step 4: card details live only in Stripe's element and
        // cannot be restored, so a resumed checkout re-enters them there.
        const paymentIndex = CHECKOUT_STEPS.indexOf('payment');
        let step = CHECKOUT_STEPS[Math.min(Math.max(server.step, 2) - 1, paymentIndex)];
        if (!cat.accelerator) {
          // No Accelerator price here: its step is skipped and any earlier
          // selection is dropped on the server too, so the invoice can be built.
          if (step === 'accelerator') step = 'payment';
          if (server.acceleratorSelected) {
            await checkoutApi.accelerator(false).catch(() => undefined);
            server = { ...server, acceleratorSelected: false };
          }
        }
        const base = stored ?? emptyDraft();
        const next: CheckoutDraft = {
          ...base,
          step,
          login: { ...base.login, email: server.email, firstName: server.firstName, lastName: server.lastName },
          contact: server.contact ? { ...base.contact, ...(server.contact as Partial<ContactDraft>) } : base.contact,
          accelerator: { ...base.accelerator, added: server.acceleratorSelected },
          appliedCoupon: server.couponCode ?? '',
        };
        if (server.plan) setPlanKey(server.plan.key);
        setDraft(next);
        if (step !== 'login') setResumed(next);
        if (server.couponCode) {
          const c = await checkoutApi.applyCoupon(server.couponCode).catch(() => null);
          if (!cancelled && c?.valid) setCoupon(toCouponDefinition(c));
        }
        return;
      }
      // The server no longer knows this checkout: keep what was typed, restart at step 1.
      if (stored) setDraft({ ...stored, step: 'login' });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist after every change so nothing is lost mid-session.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (purchased) return;
    saveDraft(draft);
  }, [draft, purchased]);

  // Move focus to the step announcement on every step change, but not on load.
  const previousStep = useRef<CheckoutStepId>(draft.step);
  useEffect(() => {
    if (previousStep.current !== draft.step) {
      previousStep.current = draft.step;
      headingRef.current?.focus();
    }
  }, [draft.step]);

  const patch = <K extends 'login' | 'contact' | 'accelerator' | 'agreement'>(
    key: K,
    value: Partial<CheckoutDraft[K]>,
  ) => setDraft((current) => ({ ...current, [key]: { ...current[key], ...value } }));

  const goToStep = (step: CheckoutStepId) => {
    setResumeDismissed(true);
    setSaveError(null);
    setDraft((current) => ({ ...current, step }));
  };

  const stepIndex = CHECKOUT_STEPS.indexOf(draft.step);
  /** The step after / before `from`, skipping the Accelerator when it has no price. */
  const neighbour = (from: number, dir: 1 | -1): CheckoutStepId => {
    let i = Math.min(Math.max(from + dir, 0), CHECKOUT_STEPS.length - 1);
    if (CHECKOUT_STEPS[i] === 'accelerator' && !acceleratorAvailable) i = Math.min(Math.max(i + dir, 0), CHECKOUT_STEPS.length - 1);
    return CHECKOUT_STEPS[i];
  };
  const goNext = () => goToStep(neighbour(stepIndex, 1));
  const goBack = () => goToStep(neighbour(stepIndex, -1));

  /** Saves on the server, then moves on; a failure keeps the member on the step with a message. */
  const saveThenNext = async (save: () => Promise<unknown>) => {
    setSaveError(null);
    try {
      await save();
      goNext();
    } catch (error) {
      setSaveError(messageOf(error));
    }
  };

  // Entering step 4 (again) makes sure the subscription matches the order; the
  // server reuses it when nothing changed.
  const preparePayment = useCallback(async () => {
    setPreparing(true);
    setPrepareError(null);
    try {
      const result = await checkoutApi.payment();
      setPayment(result);
    } catch (error) {
      setPrepareError(messageOf(error));
    } finally {
      setPreparing(false);
    }
  }, []);

  useEffect(() => {
    if (draft.step === 'payment' && catalogue && !purchased) void preparePayment();
  }, [draft.step, catalogue, purchased, preparePayment]);

  const applyCoupon = async (code: string): Promise<string | null> => {
    try {
      const result = await checkoutApi.applyCoupon(code);
      if (!result.valid) return result.reason;
      setCoupon(toCouponDefinition(result));
      setDraft((current) => ({ ...current, appliedCoupon: result.code }));
      // The order changed: the card step must be redone against the new total.
      if (payment) {
        setPayment(null);
        if (draft.step === 'complete') goToStep('payment');
        else if (draft.step === 'payment') void preparePayment();
      }
      return null;
    } catch (error) {
      if (error instanceof CheckoutApiError && error.status === 401) {
        return 'Start your checkout first, then apply the code.';
      }
      return messageOf(error);
    }
  };

  const removeCoupon = async () => {
    try {
      await checkoutApi.removeCoupon();
    } catch {
      /* the next payment step re-reads the session anyway */
    }
    setCoupon(null);
    setDraft((current) => ({ ...current, appliedCoupon: '' }));
    if (payment) {
      setPayment(null);
      if (draft.step === 'complete') goToStep('payment');
      else if (draft.step === 'payment') void preparePayment();
    }
  };

  /** Declined / errored charges send them back to payment with their data intact. */
  const failPayment = (title: string, message: string) => {
    setPurchaseStatus('idle');
    setPaymentFailure({ title, message });
    goToStep('payment');
  };

  const waitForPaid = async (): Promise<boolean> => {
    for (let i = 0; i < PAID_POLL_ATTEMPTS; i++) {
      try {
        const s = await checkoutApi.status();
        if (s.status === 'paid') return true;
      } catch {
        /* keep polling; a blip must not look like a failure */
      }
      await new Promise((resolve) => setTimeout(resolve, PAID_POLL_MS));
    }
    return false;
  };

  const completePurchase = async () => {
    // No prepared payment (e.g. it was rebuilt after an order change): back to
    // step 4. Never poll or say "payment in hand" without a confirmed charge.
    if (!payment) {
      failPayment('Enter your payment details again', 'Your order changed, so the payment form needs your card again.');
      return;
    }
    setPurchaseStatus('submitting');
    try {
      await checkoutApi.agreement(draft.agreement.termsAccepted, draft.agreement.marketingOptIn);
    } catch (error) {
      setPurchaseStatus('idle');
      setSaveError(messageOf(error));
      return;
    }

    if (payment.clientSecret) {
      const { stripe, elements } = bridge.current;
      if (!stripe || !elements) {
        failPayment('We couldn’t reach the payment processor', 'The payment form didn’t load.');
        return;
      }
      // Stripe runs any 3D Secure challenge itself; redirect only for payment
      // methods that need one (cards never do).
      const { error } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: `${window.location.origin}/checkout` },
      });
      if (error) {
        failPayment(
          error.type === 'card_error' || error.type === 'validation_error'
            ? 'Payment failed — please try again'
            : 'We couldn’t reach the payment processor',
          error.message ?? 'The payment did not go through.',
        );
        return;
      }
    }

    if (await waitForPaid()) {
      setPurchaseStatus('idle');
      clearDraft();
      setPurchased(true);
      return;
    }
    setPurchaseStatus('processing');
    clearDraft();
  };

  const backToPlans = () => setLocation('/landing');

  if (catalogueError || !catalogue || !plan || !order) {
    return (
      <div className="mx-auto max-w-xl p-8">
        {catalogueError ? (
          <CheckoutAlert tone="error" title="Checkout isn’t available right now">
            {catalogueError}
          </CheckoutAlert>
        ) : (
          <p role="status" className="text-sm text-[#7a6a8c]">
            Loading checkout…
          </p>
        )}
      </div>
    );
  }

  const resumeBanner =
    resumed && !resumeDismissed ? (
      <CheckoutAlert
        tone="success"
        title={`Welcome back${resumed.login.firstName ? `, ${resumed.login.firstName}` : ''} — picking up where you left off`}
        actions={
          <>
            <button type="button" onClick={() => setResumeDismissed(true)} className={buttonClass('sm')}>
              Continue
            </button>
            <button type="button" onClick={() => goToStep('contact')} className={buttonClass('smOutline')}>
              Review earlier steps
            </button>
          </>
        }
      >
        You stopped on step {CHECKOUT_STEPS.indexOf(resumed.step) + 1} of {CHECKOUT_STEPS.length}.
        Everything you entered is saved.
      </CheckoutAlert>
    ) : null;

  const summary = (
    <OrderSummaryCard
      plan={plan}
      order={order}
      coupon={coupon}
      onApplyCoupon={applyCoupon}
      onRemoveCoupon={() => void removeCoupon()}
      stripeDueNowCents={payment ? payment.amountDueCents : null}
    />
  );

  if (purchased) {
    return (
      <CheckoutShell step="complete" summary={summary} onBackToPlans={backToPlans}>
        <CheckoutConfirmation
          email={draft.login.email}
          order={payment ? { ...order, dueNowCents: payment.amountDueCents } : order}
          onGoToSignIn={() => setLocation('/login')}
        />
      </CheckoutShell>
    );
  }

  const cardReady = Boolean(payment?.clientSecret && stripePromise);
  const noPaymentDue = Boolean(payment && !payment.clientSecret);
  const showPaymentStep = draft.step === 'payment' || (draft.step === 'complete' && cardReady);

  const steps = (
    <>
      {cardReady ? <StripeBridgeSetter bridge={bridge} /> : null}

      {draft.step === 'login' ? (
        <LoginStep
          value={draft.login}
          onChange={(value) => patch('login', value)}
          onNext={goNext}
          onSignIn={() => setLocation('/login')}
          onCreateAccount={async () => {
            // Back on step 1 of a checkout this browser already holds: keep it.
            // Restarting would abandon what was saved (and, with a login, refuse
            // the account as existing).
            const current = await checkoutApi.status().catch(() => null);
            const email = draft.login.email.trim().toLowerCase();
            if (current && current.status !== 'paid' && current.email.trim().toLowerCase() === email) {
              return 'started';
            }
            const result = await checkoutApi.start({
              planKey: plan.id,
              email: draft.login.email,
              firstName: draft.login.firstName,
              lastName: draft.login.lastName,
              password: draft.login.password,
            });
            return result.status;
          }}
        />
      ) : null}

      {draft.step === 'contact' ? (
        <ContactInformationStep
          value={draft.contact}
          onChange={(value) => patch('contact', value)}
          onNext={() => void saveThenNext(() => checkoutApi.contact(contactPayload(draft.contact)))}
          onBack={goBack}
        />
      ) : null}

      {draft.step === 'accelerator' && acceleratorAvailable ? (
        <AcceleratorStep
          value={draft.accelerator}
          onChange={(value) => patch('accelerator', value)}
          onNext={(added) =>
            void saveThenNext(async () => {
              await checkoutApi.accelerator(added);
              // The order may have changed: step 4 must build a fresh payment
              // rather than show or confirm the old subscription's total.
              setPayment(null);
            })
          }
          onBack={goBack}
          setupFeeCents={plan.setupFeeCents}
          acceleratorCents={acceleratorCents ?? 0}
        />
      ) : null}

      {showPaymentStep ? (
        <PaymentStep
          visible={draft.step === 'payment'}
          cardReady={cardReady}
          noPaymentDue={noPaymentDue}
          preparing={preparing}
          prepareError={prepareError}
          onRetryPrepare={() => void preparePayment()}
          validateCard={async () => {
            const elements = bridge.current.elements;
            if (!elements) return 'The payment form didn’t load. Refresh the page and try again.';
            const { error } = await elements.submit();
            return error ? (error.message ?? 'Check your card details.') : null;
          }}
          onNext={goNext}
          onBack={goBack}
          failure={paymentFailure}
          onDismissFailure={() => setPaymentFailure(null)}
        />
      ) : null}

      {draft.step === 'complete' ? (
        <CompletePurchaseStep
          plan={plan}
          order={payment ? { ...order, dueNowCents: payment.amountDueCents } : order}
          value={draft.agreement}
          onChange={(value) => patch('agreement', value)}
          onBack={goBack}
          onSubmit={() => void completePurchase()}
          onConfirmAuthentication={() => undefined}
          status={purchaseStatus}
        />
      ) : null}
    </>
  );

  return (
    <CheckoutShell step={draft.step} banner={resumeBanner} summary={summary} onBackToPlans={backToPlans}>
      {/* Focus target so each step change is announced. */}
      <div ref={headingRef} tabIndex={-1} className="sr-only">{`Step ${stepIndex + 1} of ${CHECKOUT_STEPS.length}: ${STEP_LABEL[draft.step]}`}</div>

      {saveError ? (
        <CheckoutAlert tone="error" title="We couldn’t save that step">
          {saveError}
        </CheckoutAlert>
      ) : null}

      {cardReady ? (
        <Elements
          key={payment!.clientSecret!}
          stripe={stripePromise}
          options={{
            clientSecret: payment!.clientSecret!,
            appearance: { theme: 'stripe', variables: { colorPrimary: '#4b0a5b', borderRadius: '10px' } },
          }}
        >
          {steps}
        </Elements>
      ) : (
        steps
      )}
    </CheckoutShell>
  );
}
